/*
 * TODO
 * - check technical drawing image
 *
 * the controller connects the model and view
 * it handles events
 */
import './model-finder.scss';

import { createElementFromHTML } from '../../js/helper';

import { ModelFinderView } from './model-finder-view';
import { ModelFinderModel } from './model-finder-model';
import { ModelFinderTracking } from './model-finder-tracking';

class ModelFinderController {
    constructor ($modelFinder, options) {
        // DOM elements
        this.settings = Object.assign({}, {}, options);
        this.$modelFinder = $modelFinder;
        this.$form = $modelFinder.querySelector('[data-model-finder="form"]');
        this.$steps = $modelFinder.querySelectorAll('[data-model-finder="step"]');
        this.$resetButton = $modelFinder.querySelector('[data-model-finder="reset"]');
        this.$firstMeaningfulStep = null; // first step which has more than 1 option
        this.$result = $modelFinder.querySelector('[data-model-finder="result"]');

        // tracking class
        this.modelFinderTracking = new ModelFinderTracking($modelFinder);

        // model and view class
        this.modelFinderView = new ModelFinderView($modelFinder, {
            breakpoints: options.breakpoints,
            modelFinderTracking: this.modelFinderTracking,
            onUpdate: this.settings.onUpdate
        });
        this.modelFinderModel = new ModelFinderModel($modelFinder);

        // control variables
        this.isLoading = false; // check if an ajax request is running
        this.clickedStepNumber = 0;
        this.totalNumberOfSteps = this.$steps.length;
        this.requestTimestamp = undefined;

        this.resizeHandler = options.resizeHandler;
    }

    // module entry point
    init () {
        this.initFilter();
        this.initResult();
        this.bindModuleEvents();
        this.modelFinderView.checkForInitialFinishedConfig();

        this.modelFinderView.handleStickybits();
    }

    bindModuleEvents () {
        // window.addEventListener('resize', () => {
        //     clearTimeout(this.modelFinderTimer);
        //     this.modelFinderTimer = window.setTimeout(() => {
        //         // this.modelFinderView.handleScrollLock();
        //         this.modelFinderView.setEqualHeight();
        //     }, 250);
        // });
        this.resizeHandler.customFunctions.push(() => {
            this.modelFinderView.setEqualHeight();
        });
    }

    /*
     * filter related controls
     */

    initFilter () {
        this.setFirstMeaningfulStep();
        this.bindFilterEvents();
    }

    bindFilterEvents () {
        if (this.$form) {
            this.$form.addEventListener('change', (event) => {
                const eventTarget = event.target;
                const $step = eventTarget.closest('[data-step-number]');

                this.modelFinderTracking.trackOption($step);

                if (eventTarget.getAttribute('data-model-finder') === 'radio' && !this.isLoading) {
                    this.isLoading = true;
                    this.setClickedStepNumber($step);
                    this.modelFinderView.setTextForOptionPlaceholder(this.clickedStepNumber);
                    this.requestTimestamp = Date.now();
                    this.modelFinderView.setResultLoadingAnimation(true);

                    // make ajax request
                    this.modelFinderModel.requestData(eventTarget).then(() => {
                        this.modelFinderView.isFinished = this.checkIfFinished();

                        // filter stuff
                        this.updateFilter();

                        // scroll to new active step
                        const $activeStep = this.$modelFinder.querySelector('.model-finder__step--active');
                        this.modelFinderView.scrollToStep($activeStep);

                        this.modelFinderTracking.$recentStep = $activeStep;
                        this.modelFinderTracking.trackLoadedStep($activeStep);

                        // result stuff
                        this.updateResult();

                        if (typeof this.settings.onUpdate === 'function') {
                            this.settings.onUpdate(this.$modelFinder);
                        }

                        // update product support teaser
                        this.updateProductSupport();
                    }).catch((e) => {
                        console.warn('error occurred: ');
                        console.warn(e);
                    }).finally(() => {
                        this.isLoading = false;
                    });
                }
            });
        }

        const addStepEvent = ($step) => {
            $step.addEventListener('click', (event) => {
                const eventTarget = event.target;

                // when glossary is clicked, do nothing in the model finder
                if (eventTarget.getAttribute('data-glossary-init') === 'init') {
                    event.preventDefault();
                    return false;
                }
                this.modelFinderTracking.trackStep($step);

                if (!eventTarget.classList.contains('model-finder__label') &&
                    !eventTarget.classList.contains('model-finder__radio') &&
                    ($step.classList.contains('model-finder__step--filled') || $step.classList.contains('model-finder__step--active'))
                ) {
                    // activate step
                    // const $step = event.currentTarget;
                    this.setClickedStepNumber($step);
                    // the order of function calls is important, change with caution
                    if (($step.classList.contains('model-finder__step--filled')) ||
                        // this condition prevents scrollToStep when the step is already active in not active state
                        ($step.classList.contains('model-finder__step--active') && this.$modelFinder.classList.contains('model-finder--active'))
                    ) {
                        // wait for active state animation finish, when coming from active state
                        if (this.$modelFinder.classList.contains('model-finder--active')) {
                            setTimeout(() => {
                                this.modelFinderView.scrollToStep($step);
                            }, 175);
                        } else {
                            this.modelFinderView.scrollToStep($step);
                        }
                    }
                    this.modelFinderView.activateClickedStep($step);
                    this.modelFinderView.setResultVisibility(false);
                    // this.modelFinderView.handleScrollLock();
                }
            }, { passive: true });
        };

        for (let i = 0; i < this.totalNumberOfSteps; i++) {
            const $step = this.$steps[i];
            addStepEvent($step);
        }

        if (this.$resetButton) {
            this.$resetButton.addEventListener('click', (e) => {
                e.preventDefault();
                // tracking
                this.modelFinderTracking.trackReset();
                // reset filter
                this.modelFinderView.resetFilter(this.$firstMeaningfulStep);
                this.modelFinderView.setFinishTextVisibility(false);
                this.modelFinderView.setResetButtonVisibility(false);
                this.modelFinderView.setResultVisibility(false);
                this.modelFinderView.scrollToTopOfElement(this.$modelFinder);

                // reset result (send request without data)
                this.modelFinderModel.requestData().then(() => {
                    // result stuff
                    this.updateResult();

                    // update product support teaser
                    this.updateProductSupport();

                    if (typeof this.settings.onUpdate === 'function') {
                        this.settings.onUpdate(this.$modelFinder);
                    }
                }).catch((e) => {
                    console.warn('error occurred: ');
                    console.warn(e);
                }).finally(() => {
                    this.isLoading = false;
                });
            }, { passive: true });
        }
    }

    setClickedStepNumber ($step) {
        this.clickedStepNumber = parseInt($step.getAttribute('data-step-number'));
        // update clicked step number for model class
        this.modelFinderModel.clickedStepNumber = this.clickedStepNumber;
        this.modelFinderView.clickedStepNumber = this.clickedStepNumber;
    }

    updateFilter () {
        const numberOfIncomingSteps = this.modelFinderModel.newStepsAndOptions.length;

        // condition when we did not reached the end, because we still get new step data
        if (numberOfIncomingSteps >= 1) {
            this.modelFinderView.clearSelectedOptionPlaceholderRange(this.clickedStepNumber + numberOfIncomingSteps - 1, this.totalNumberOfSteps - 1);
            this.modelFinderView.insertNewOptions(this.modelFinderModel.newStepsAndOptions);

            this.modelFinderView.setPreFinishTextVisibility(false);
            this.modelFinderView.setFinishTextVisibility(false);
            this.modelFinderView.setResetButtonVisibility(false);

            // reached last step (but not answered yet)
            if (this.checkIfLastStepIsActive()) {
                this.modelFinderView.setPreFinishTextVisibility(true);
            }
        }

        // when we are done with all steps
        if (this.checkIfFinished()) {
            this.modelFinderView.setPreFinishTextVisibility(false);
            this.modelFinderView.setFinishTextVisibility(true);
            this.modelFinderView.setResetButtonVisibility(true);
            this.modelFinderTracking.trackFinish();

            // maybe put this in a separate function???
            // or split updateFilter into smaller functions
            setTimeout(() => {
                this.modelFinderView.setResultVisibility(true);
                // this.modelFinderView.handleScrollLock();
            }, 1000);
        }
    }

    checkIfLastStepIsActive () {
        const filterMarkup = this.modelFinderModel.ajaxData.filter;
        const steps = createElementFromHTML(filterMarkup);

        if (steps[steps.length - 1].classList.contains('model-finder__step--active')) {
            return true;
        } else {
            return false;
        }
    }

    checkIfFinished () {
        const numberOfIncomingSteps = this.modelFinderModel.newStepsAndOptions.length;
        const stepsAndOptions = this.modelFinderModel.newStepsAndOptions;
        const lastStep = stepsAndOptions[stepsAndOptions.length - 1];

        // if no more steps follow
        // or the last step in the array has only 1 option, it means we are done
        if (numberOfIncomingSteps === 0 || lastStep.length === 1) {
            return true;
        } else {
            return false;
        }
    }

    setFirstMeaningfulStep () {
        this.$firstMeaningfulStep = this.$modelFinder.querySelector('.model-finder__step--active');
    }

    /*
     * result related controls
     */

    initResult () {
        this.modelFinderView.initSlider();
        this.modelFinderView.lockSlider();
        this.modelFinderView.resetResultScrollPosition();

        this.bindResultEvents();
        // need to wait until css is applied, maybe there is a better solution
        setTimeout(() => {
            this.modelFinderView.setEqualHeight();
        }, 200);
    }

    bindResultEvents () {
        const $resultToggle = this.$modelFinder.querySelector('[data-model-finder="toggle"]');

        if ($resultToggle) {
            $resultToggle.addEventListener('click', () => {
                this.modelFinderTracking.trackOverlayToggle();
                this.modelFinderView.setResultVisibility();
                // this.modelFinderView.handleScrollLock();
            }, { passive: true });
        }
    }

    updateResult () {
        // keep the code below, until we decide how long the animation should
        // kept visible

        // const now = Date.now();
        // const requestTimeDifference = now - this.requestTimestamp;
        // const minimalWaitingTime = 1000;
        const executeUpdate = () => {
            const resultMarkup = this.modelFinderModel.ajaxData.result;

            this.modelFinderView.renderResult(resultMarkup);
            this.checkForOuttra();
            this.initResult();

            this.modelFinderView.setResultLoadingAnimation(false);
        };

        executeUpdate();

        // if (requestTimeDifference >= minimalWaitingTime) {
        //     executeUpdate();
        //     console.log('not in timeout');
        // } else {
        //     setTimeout(() => {
        //         executeUpdate();
        //         console.log('in timeout (' + (minimalWaitingTime - requestTimeDifference) + 'ms)');
        //     }, minimalWaitingTime - requestTimeDifference);
        // }
    }

    checkForOuttra () {
        const scripts = this.$result.querySelectorAll('[data-gtins]');
        const loadScript = (ean, lang, $target) => {
            return new Promise((resolve, reject) => {
                let script = document.createElement('script');
                const source = 'https://brandwidgetstest.outtra.com/js/dtswiss/button-widget.js';

                script.async = true;
                script.setAttribute('data-outtrabrand_id', '199');
                script.setAttribute('data-gtins', ean);
                script.setAttribute('data-lang', lang);

                function onloadHander (_, isAbort) {
                    if (isAbort || !script.readyState || /loaded|complete/.test(script.readyState)) {
                        script.onload = resolve(script);
                        script.onreadystatechange = null;
                        script = undefined;

                        if (isAbort) {
                            const error = new Error('something went wrong');
                            reject(error);
                        } else {
                            resolve(script);
                        }
                    }
                }

                script.onload = onloadHander;
                script.onreadystatechange = onloadHander;

                script.src = source;
                $target.parentNode.insertBefore(script, $target);
            });
        };

        if (scripts.length > 0) {
            scripts.forEach((script) => {
                const ean = script.dataset.gtins;
                const lang = script.dataset.lang;

                loadScript(ean, lang, script).then(() => {
                    const $parent = script.parentElement;

                    script.remove();

                    const contentUpdateEvent = new CustomEvent('content.loaded', {
                        view: window,
                        bubbles: true,
                        cancelable: false,
                        detail: {
                            $context: $parent
                        }
                    });

                    window.dispatchEvent(contentUpdateEvent);
                });
            });
        }
    }

    updateProductSupport () {
        const productSupportMarkup = this.modelFinderModel.ajaxData.support;

        const $productSupportWrapper = document.querySelector('[data-model-finder-product-support]');
        if ($productSupportWrapper) {
            $productSupportWrapper.innerHTML = productSupportMarkup;

            const productSupportEvent = new CustomEvent('content.loaded', {
                view: window,
                bubbles: true,
                cancelable: false,
                detail: {
                    $context: $productSupportWrapper
                }
            });

            window.dispatchEvent(productSupportEvent);
        }
    }
}

export { ModelFinderController };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $modelFinder = $context.querySelector('[data-model-finder="root"]');

        if ($modelFinder) {
            const modelFinder = new ModelFinderController($modelFinder, {
                breakpoints: window.OPTIONS.breakpoints,
                onUpdate: ($elements) => {
                    window.initContent($elements);
                },
                resizeHandler: window.resizeHandler
            });
            modelFinder.init();
        }
    }
});
